import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { LanguageCode, Theme } from '@/types/api';
import i18n from '@/app/i18n';

interface PreferencesState {
  theme: Theme;
  uiLanguage: LanguageCode;
}

interface PreferencesActions {
  setTheme: (theme: Theme) => void;
  toggleTheme: () => void;
  setUiLanguage: (language: LanguageCode) => void;
}

type PreferencesStore = PreferencesState & PreferencesActions;

/**
 * Apply theme to document
 */
function applyTheme(theme: Theme): void {
  document.documentElement.setAttribute('data-theme', theme);
}

/**
 * Apply language to i18n
 */
function applyLanguage(language: LanguageCode): void {
  i18n.changeLanguage(language);
}

/**
 * Preferences store - persisted to localStorage
 * Manages theme and UI language preferences
 */
export const usePreferencesStore = create<PreferencesStore>()(
  persist(
    (set, get) => ({
      // Initial state
      theme: 'light',
      uiLanguage: 'en',

      // Actions
      setTheme: (theme: Theme) => {
        applyTheme(theme);
        set({ theme });
      },

      toggleTheme: () => {
        const newTheme = get().theme === 'light' ? 'dark' : 'light';
        applyTheme(newTheme);
        set({ theme: newTheme });
      },

      setUiLanguage: (language: LanguageCode) => {
        applyLanguage(language);
        set({ uiLanguage: language });
      },
    }),
    {
      name: 'preferences-storage',
      // On rehydrate, apply theme and language
      onRehydrateStorage: () => (state) => {
        if (state) {
          applyTheme(state.theme);
          applyLanguage(state.uiLanguage);
        }
      },
    }
  )
);

/**
 * Initialize preferences on app start
 * Call this in App.tsx to ensure theme/language are applied
 */
export function initializePreferences(): void {
  const state = usePreferencesStore.getState();
  applyTheme(state.theme);
  applyLanguage(state.uiLanguage);
}
